import React, { useState, useReducer, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Button from '@strava/ui/Button';
import I18n from 'utils/I18n';
import SvgActionsKudoNormalXsmall from '@strava/icons/ActionsKudoNormalXsmall';
import SvgActionsKudoHighlightedXsmall from '@strava/icons/ActionsKudoHighlightedXsmall';
import colors from '@strava/ui/colors';
import createNetworkingClient from 'utils/networking-client';
import SvgActionsCommentNormalXsmall from '@strava/icons/ActionsCommentNormalXsmall';
import { devLog } from 'utils/dev-utils';
import Cldr from 'utils/Cldr';
import { snakeCase } from 'lodash-es';
import {
  ADD_KUDO,
  OPEN_MODAL_COMMENTS_TAB,
  OPEN_MODAL_KUDOS_TAB
} from '../../../components/shared/KudosCommentsAndAchievements/kudosAndComments.actions';
import styles from './styles.scss';
import { updateKudosEndpoint } from '../../../components/shared/utils/updateKudosEndpoint';
import KudosAndCommentsContext from '../../../components/shared/KudosCommentsAndAchievements/KudosAndCommentsContext';
import {
  initKudosAndCommentsState,
  kudosAndCommentsReducer
} from '../../../components/shared/KudosCommentsAndAchievements/kudosAndComments.reducer';
import { buildInitialKudosAndCommentsState } from '../../../components/shared/KudosCommentsAndAchievements/kudosAndComments.initial-state';
import KudosCommentsAndAchievementsModal from '../../../components/shared/KudosCommentsAndAchievements/KudosCommentsAndAchievementsModal/KudosCommentsAndAchievementsModal';
import {
  commentsProps,
  entitiesWithKudosAndComments,
  viewingAthleteProps
} from '../../../components/Feed/props/feed-prop-types';
import CommentDeleteAlertDialog from '../../../components/shared/KudosCommentsAndAchievements/CommentDeleteAlertDialog';

const ADPKudosAndComments = ({
  canKudo,
  commentsEnabled,
  comments,
  kudosCount,
  entity,
  entityId,
  ownerName,
  ownerAvatarUrl,
  ownedByCurrentAthlete,
  viewingAthlete,
  activity,
  reactionsEnabled,
  undoCommentReactionsEnabled
}) => {
  const [canKudoStatus, setCanKudoStatus] = useState(canKudo);
  const [currentKudoCount, setCurrentKudoCount] = useState(kudosCount);
  const [hasKudoed, setHasKudoed] = useState(false);
  const entityEndpoint = updateKudosEndpoint({ entity });
  const prefix = 'components.feed.social';

  // We need to provide a trackAnalytics function to buildInitialKudosAndCommentsState,
  // but we are using trackv2 for analytics directly.
  const trackAnalytics = () => {};

  const [kudosAndCommentsEntityState, dispatch] = useReducer(
    kudosAndCommentsReducer,
    buildInitialKudosAndCommentsState({
      trackAnalytics,
      comments: { [entityId]: comments },
      entity: snakeCase(entity),
      entityId
    }),
    initKudosAndCommentsState
  );

  const providerState = {
    kudosAndCommentsEntityState,
    dispatch
  };

  // Using comments from kudosAndCommentsEntityState rather than ruby props
  // allows us to know when a new comment has been posted
  const entityStateComments = kudosAndCommentsEntityState.comments[entityId];
  const commentReactionAnalytics = {
    category: 'activity_detail',
    page: 'comment',
    buttonElement: 'like_comment_btn_on_modal',
    listElement: 'like_list_btn_on_modal'
  };
  const activityWorkoutType = Object.keys(activity);
  const activityDetails = activity[activityWorkoutType];
  const entityOwner = {
    displayImage: ownerAvatarUrl,
    name: ownerName,
    profileUrl: `/athletes/${activityDetails.athlete_id}`,
    type: 'athlete'
  };

  const onClickKudoSuccessful = useCallback(() => {
    // Not allowed to kudo more than once
    setCanKudoStatus(false);
    setHasKudoed(true);
  }, []);

  useEffect(() => {
    if (kudosAndCommentsEntityState.kudoPosted) {
      setCurrentKudoCount((prev) => prev + 1);
      onClickKudoSuccessful();
    }
  }, [kudosAndCommentsEntityState.kudoPosted, onClickKudoSuccessful, dispatch]);

  // Renders the modal if url includes #comments or #kudos
  // url comes from notification links
  useEffect(() => {
    if (window.location.href.includes('#comments')) {
      dispatch({
        type: OPEN_MODAL_COMMENTS_TAB,
        payload: { element: 'comment', selectedEntityId: entityId }
      });
    } else if (window.location.href.includes('#kudos')) {
      dispatch({
        type: OPEN_MODAL_KUDOS_TAB,
        payload: {
          element: 'kudo_icon',
          selectedEntityId: entityId
        }
      });
    }
  }, [entityId]);

  const onClickKudo = () => {
    createNetworkingClient()
      .post(`/feed/${entityEndpoint}/${entityId}/kudo`)
      .then((response) => {
        if (response && response.status === 200) {
          onClickKudoSuccessful();
        }
      })
      .then(() => {
        dispatch({ type: ADD_KUDO });
      })
      .catch((e) => {
        devLog(e);
      });
  };

  return (
    <KudosAndCommentsContext.Provider value={providerState}>
      <React.Suspense fallback={<div />}>
        <div className={styles.adpKudosCommentsContainer}>
          <div className={styles.kudosButtonContainer}>
            {canKudoStatus && (
              <Button
                className={styles.giveKudosBtn}
                onClick={() => onClickKudo()}
                data-testid="give-kudos-btn"
              >
                {I18n.t(`${prefix}.icon.give_kudos`)}
              </Button>
            )}
            <Button
              type="button"
              variant="default"
              className={styles.socialButton}
              title={I18n.t(`${prefix}.view_all_kudos`)}
              onClick={() =>
                dispatch({
                  type: OPEN_MODAL_KUDOS_TAB,
                  payload: {
                    element: 'kudo_icon',
                    selectedEntityId: entityId
                  }
                })
              }
              data-testid="adp-kudos_button"
            >
              {canKudoStatus || ownedByCurrentAthlete ? (
                <SvgActionsKudoNormalXsmall
                  fill={colors.N70_GRAVEL}
                  data-testid="unfilled_kudos"
                  className={styles.unfilledKudoIcon}
                />
              ) : (
                <SvgActionsKudoHighlightedXsmall
                  className={hasKudoed ? styles.kudoedIcon : styles.kudoIcon}
                  fill={colors.O50_STRAVA_ORANGE}
                  data-testid="filled_kudos"
                />
              )}
              <div title={I18n.t(`${prefix}.view_all_kudos`)}>
                <span data-testid="kudos_count" className={styles.count}>
                  {currentKudoCount}
                </span>
              </div>
            </Button>
          </div>

          {commentsEnabled && (
            <Button
              type="button"
              variant="default"
              className={styles.socialButton}
              data-testid="open_comment_modal_button"
              onClick={() =>
                dispatch({
                  type: OPEN_MODAL_COMMENTS_TAB,
                  payload: { element: 'comment', selectedEntityId: entityId }
                })
              }
              title={I18n.t(`${prefix}.view_all_comments`)}
            >
              <SvgActionsCommentNormalXsmall className={styles.commentIcon} />
              <span data-testid="comments_count" className={styles.count}>
                {Cldr.formatDecimal(entityStateComments.length)}
              </span>
            </Button>
          )}
          {(kudosAndCommentsEntityState.openKudosCommentModal ||
            kudosAndCommentsEntityState.isViewingCommentReactionsList) &&
            kudosAndCommentsEntityState.selectedEntityId === entityId && (
              <KudosCommentsAndAchievementsModal
                entity={entity}
                entityId={entityId}
                entityOwner={entityOwner}
                hasOtherAthletes={entity === 'group_activity'}
                title={activityDetails.name}
                viewingAthlete={viewingAthlete}
                kudosCount={currentKudoCount}
                commentsEnabled={commentsEnabled}
                segAndBestEffortAchievements={[]}
                reactionsEnabled={reactionsEnabled}
                undoCommentReactionsEnabled={undoCommentReactionsEnabled}
                commentReactionAnalytics={commentReactionAnalytics}
              />
            )}
          <CommentDeleteAlertDialog />
        </div>
      </React.Suspense>
    </KudosAndCommentsContext.Provider>
  );
};

ADPKudosAndComments.propTypes = {
  canKudo: PropTypes.bool.isRequired,
  comments: commentsProps.isRequired,
  kudosCount: PropTypes.number.isRequired,
  entityId: PropTypes.string.isRequired,
  ownerName: PropTypes.string.isRequired,
  ownerAvatarUrl: PropTypes.string.isRequired,
  ownedByCurrentAthlete: PropTypes.bool.isRequired,
  commentsEnabled: PropTypes.bool.isRequired,
  entity: entitiesWithKudosAndComments.isRequired,
  viewingAthlete: viewingAthleteProps.isRequired,
  activity: PropTypes.shape({}).isRequired,
  reactionsEnabled: PropTypes.bool.isRequired,
  undoCommentReactionsEnabled: PropTypes.bool.isRequired
};

export default ADPKudosAndComments;
